import { useContext, useEffect, useState } from 'react';

import { UserContext } from '@adflow/utils';

export const TEMPLATE_BUILDER_EDITOR_CANVAS_FEATURE = 'cm-template-builder-editor-canvas';

export const TEMPLATE_EDITOR_CANVAS_CYPRESS_OVERRIDE_KEY = 'cm-template-builder-editor-canvas';

type WindowWithCypress = Window & { Cypress?: unknown };

function isRunningInsideCypress(): boolean {
  return typeof window !== 'undefined' && Boolean((window as WindowWithCypress).Cypress);
}

function readCypressOverride(): boolean | null {
  if (!isRunningInsideCypress()) {
    return null;
  }

  const value = window.sessionStorage.getItem(TEMPLATE_EDITOR_CANVAS_CYPRESS_OVERRIDE_KEY);
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }

  return null;
}

export async function resolveTemplateEditorCanvasFeatureEnabled(
  user: { hasFeature?: (feature: string) => Promise<boolean> } | null | undefined,
): Promise<boolean> {
  const cypressOverride = readCypressOverride();
  if (cypressOverride != null) {
    return cypressOverride;
  }

  return (await user?.hasFeature?.(TEMPLATE_BUILDER_EDITOR_CANVAS_FEATURE)) ?? false;
}

export function readTemplateEditorCanvasCypressOverride(): boolean | null {
  return readCypressOverride();
}

export function useTemplateEditorCanvasFeature(): boolean {
  const { user } = useContext(UserContext);
  const [flagEnabled, setFlagEnabled] = useState(
    () => readCypressOverride() ?? import.meta.env.DEV,
  );

  useEffect(() => {
    let cancelled = false;
    const cypressOverride = readCypressOverride();
    if (cypressOverride != null) {
      setFlagEnabled(cypressOverride);
      return;
    }
    if (import.meta.env.DEV) {
      setFlagEnabled(true);
      return;
    }

    const checkFeature = async () => {
      const hasFeature = await resolveTemplateEditorCanvasFeatureEnabled(user);
      if (!cancelled) {
        setFlagEnabled(hasFeature);
      }
    };
    checkFeature().catch((error) => {
      console.warn('Failed to check template builder editor canvas feature', error);
      if (!cancelled) {
        setFlagEnabled(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  return flagEnabled;
}
